import React, { useState } from 'react';
import {
    Drawer, List, ListItem, ListItemButton, ListItemText,
    IconButton, Button, Avatar, Divider, Box,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useSelector } from 'react-redux';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../store';
import { makeLink, getAvailableLanguagesWithNames, geLanguageFromUrl, getSwitchLanguageOnCurrentUrl } from '../../common/languages-helper';
import './MobileMenu.css';

interface MenuEntry { title: string; url: string }

interface MobileMenuProps {
    firstRowMenu: MenuEntry[];
    secondRowMenu: MenuEntry[];
    isAuthenticated: boolean;
    loginFunction: any;
    logout: any;
    /** Hide auth actions inside session windows (matches the desktop header). */
    showAuth?: boolean;
}

const languages = getAvailableLanguagesWithNames();

const MobileMenu: React.FC<MobileMenuProps> = ({
    firstRowMenu, secondRowMenu, isAuthenticated, loginFunction, logout, showAuth = true,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [navigateTo, setNavigateTo] = useState('');
    const { t } = useTranslation();
    const profileData = useSelector((state: RootState) => state.profile);

    const currentLang = geLanguageFromUrl();
    const displayName = (profileData?.name || '').trim();
    const email = profileData?.email || '';
    const avatarUrl = profileData?.avatarUrl || '';
    const initial = (displayName || email || 'E').charAt(0).toUpperCase();

    const close = () => setIsOpen(false);

    const switchLanguage = (code: string) => {
        if (code === currentLang) { close(); return; }
        const target = getSwitchLanguageOnCurrentUrl(code);
        setNavigateTo(target);
        setTimeout(() => setNavigateTo(''), 100);
        close();
    };

    const navItems: MenuEntry[] = [...firstRowMenu, ...secondRowMenu];

    return (
        <>
            <IconButton
                edge="start"
                color="inherit"
                aria-label={t('Menu')}
                onClick={() => setIsOpen(true)}
            >
                <MenuIcon />
            </IconButton>

            <Drawer
                anchor="right"
                open={isOpen}
                onClose={close}
                PaperProps={{ className: 'mobile-drawer' }}
            >
                <div className="mobile-drawer-inner">
                    <div className="mobile-drawer-head">
                        <img className="mobile-drawer-logo" src="/logos/emdr-logo-new.svg" alt="EMDRlink" />
                        <IconButton aria-label={t('Close')} onClick={close} sx={{ color: 'var(--light-link)' }}>
                            <CloseIcon />
                        </IconButton>
                    </div>

                    {showAuth && (
                        <div className="mobile-drawer-account">
                            {isAuthenticated ? (
                                <>
                                    <Box className="mobile-account-id">
                                        <Avatar
                                            src={avatarUrl || undefined}
                                            className="mobile-account-avatar"
                                        >
                                            {!avatarUrl && initial}
                                        </Avatar>
                                        <Box className="mobile-account-text">
                                            {displayName && <span className="mobile-account-name">{displayName}</span>}
                                            {email && <span className="mobile-account-email">{email}</span>}
                                        </Box>
                                    </Box>
                                    <Button
                                        component={Link}
                                        to={makeLink('/profile')}
                                        onClick={close}
                                        startIcon={<AccountCircleIcon />}
                                        className="mobile-btn mobile-btn-outline"
                                        fullWidth
                                    >
                                        {t('Account settings')}
                                    </Button>
                                    <Button
                                        onClick={() => { close(); logout(); }}
                                        startIcon={<LogoutIcon />}
                                        className="mobile-btn mobile-btn-ghost"
                                        fullWidth
                                    >
                                        {t('Logout')}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button
                                        onClick={(event: any) => { close(); loginFunction(null, event); }}
                                        startIcon={<LoginIcon />}
                                        className="mobile-btn mobile-btn-primary"
                                        fullWidth
                                    >
                                        {t('Sign In')}
                                    </Button>
                                    <Button
                                        onClick={(event: any) => { close(); loginFunction({ authorizationParams: { screen_hint: 'signup' } }, event); }}
                                        startIcon={<PersonAddIcon />}
                                        className="mobile-btn mobile-btn-ghost"
                                        fullWidth
                                    >
                                        {t('Sign Up')}
                                    </Button>
                                </>
                            )}
                        </div>
                    )}

                    <Divider className="mobile-drawer-divider" />

                    <List className="mobile-drawer-nav" disablePadding>
                        {navItems.map((item, index) => {
                            const external = typeof item.url === 'string' && item.url.startsWith('http');
                            const externalProps = external ? { target: '_blank', rel: 'noopener noreferrer' } : {};
                            return (
                                <ListItem key={item.url && item.url !== '#' ? item.url : `nav-${index}`} disablePadding>
                                    <ListItemButton component="a" href={item.url} onClick={close} {...externalProps}>
                                        <ListItemText primary={item.title} />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>

                    <Divider className="mobile-drawer-divider" />

                    <div className="mobile-drawer-lang">
                        <span className="mobile-drawer-lang-label">{t('Language')}</span>
                        <div className="mobile-lang-options" role="group" aria-label={t('Language')}>
                            {languages.map((lang) => (
                                <button
                                    key={lang.code}
                                    type="button"
                                    className={'mobile-lang-chip' + (lang.code === currentLang ? ' is-active' : '')}
                                    aria-pressed={lang.code === currentLang}
                                    onClick={() => switchLanguage(lang.code)}
                                >
                                    {lang.code.toUpperCase()}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </Drawer>

            {navigateTo && <Navigate to={navigateTo} />}
        </>
    );
};

export default MobileMenu;
